import React, { useEffect, useState } from "react";
import {
    Row,
    Col,
    Typography,
    Image,
    Input,
    Divider,
    Checkbox,
    theme,
    Form,
    Button,
    notification,
    Tooltip,
    Select,
    InputNumber
} from "antd";
import styles from "../styles/InvoicePayment.module.css";
import { useBrandContext } from "../contexts/Brand";
import { useServer } from "../contexts/Server";
import {
    screenWidths,
    validateEmail,
    decodeParamString,
    encodeParamString,
    formatNumberWithCommas,
} from "../const/const_functions";
import { AiFillStar } from "react-icons/ai";
import useWindowDimensions from "../hooks/useWindowDimensions";
import { useNavigate, useParams } from "react-router-dom";
import { FaInfoCircle } from "react-icons/fa";

const allRules = {
    amountToPay: [
        {
            required: true,
            message: "Please input Payment Amount!",
        },
        {
            validator: (_, value) => {
                if (value && value <= 0) {
                    return Promise.reject(new Error("Payment amount is invalid"));
                }
                return Promise.resolve();
            },
        },
    ],
    customerEmail: [
        {
            required: true,
            message: "Please input your Email!",
        },
        { whitespace: true },
        {
            validator: (_, value) => {
                const trimmedValue = value.trim();
                if (trimmedValue && !validateEmail(trimmedValue)) {
                    return Promise.reject(new Error("Please input a valid email"));
                }

                return Promise.resolve();
            },
        },
    ],
    customerPhone: [
        { whitespace: false },
        {
            validator: (_, value) => {
                if (Number(value) < 0) {
                    return Promise.reject(
                        new Error("Please enter a valid 10 digit phone number.")
                    );
                }

                if (isNaN(Number(value))) {
                    return Promise.reject(
                        new Error("Phone Numbers should only consist of digits.")
                    );
                }

                if (value && value.length !== 10) {
                    return Promise.reject(
                        new Error("Phone Numbers should be 10 digits long.")
                    );
                }

                return Promise.resolve();
            },
        },
    ],
};

const Bird = {
    accountId: {
        label: "Account Number",
        type: "or",
        sibling: "invoiceId",
        display: true,
        allowSave: true,
    },
    invoiceId: {
        label: "Invoice Number",
        sibling: "accountId",
        type: "or",
        display: false,
        allowSave: false,
    },
    amountToPay: {
        label: "Payment Amount",
        type: "required",
        display: true,
        allowSave: true,
    },
    serviceDescription: {
        label: "Service Description",
        type: "required",
        display: true,
        allowSave: true,
    },
    customerName: {
        label: "Personal/Business Name",
        type: "required",
        display: true,
        allowSave: true,
    },
    customerEmail: {
        label: "Email",
        type: "required",
        display: true,
        allowSave: true,
    },
    customerPhone: {
        label: "Phone",
        type: "optional",
        display: true,
        allowSave: true,
    },
};

const { Text } = Typography;

const PreFilledInvoicePaymentForm = () => {
    const { brandContextData, loadingBrandContextData } = useBrandContext();
    const { serverData } = useServer();
    const { useToken } = theme;
    const navigate = useNavigate();
    const { token } = useToken();
    const [form] = Form.useForm();

    const { encodedOrderId } = useParams();

    const [stadiumData, setStadiumData] = useState(null);
    const [orderTotalWithEverything, setOrderTotalWithEverything] = useState(0);
    const [orderSubtotal, setOrderSubtotal] = useState(0);
    const [digitalFeeObj, setDigitalFeeObj] = useState(null);
    const [serviceFeeToShow, setServiceFeeToShow] = useState(0);
    const [serviceFeeAgreement, setServiceFeeAgreement] = useState(false);

    const [submittable, setSubmittable] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState({});

    let mobile = false;
    const { width } = useWindowDimensions();
    if (screenWidths.md > width) {
        mobile = true;
    }

    useEffect(() => {
        handleFormChange();
    }, [serviceFeeAgreement, digitalFeeObj]);

    useEffect(() => {
        // Sync form values with state
        form.setFieldsValue(invoiceDetails);
    }, [invoiceDetails, form]);

    useEffect(() => {
        if (serverData) {
            getOrderData();
        }
    }, [serverData]);

    const validateInvoiceDetails = (invoiceDetails) => {
        const errors = {};

        const requiredFields = new Set();

        if (Object.keys(invoiceDetails).length === 0) {
            return { noInvoiceDetails: "There are no invoice details" };
        }

        if (!stadiumData?.invoiceTemplate) {
            return errors;
        }

        Object.keys(stadiumData?.invoiceTemplate).forEach((field) => {
            const { type, sibling, display, label } = stadiumData.invoiceTemplate[field];

            if (display) {
                const value = invoiceDetails[field];

                if (type === "required" && (!value || String(value).trim() === "")) {
                    errors[field] = `${label} is required.`;
                }

                if (sibling) {
                    requiredFields.add(field);

                    if (invoiceDetails[field] && invoiceDetails[sibling]) {
                        // If both siblings are present, clear the errors for both
                        delete errors[field];
                        delete errors[sibling];
                    }
                }
            }
        });

        // Check if at least one field from each sibling pair is present
        requiredFields.forEach((field) => {
            const sibling = stadiumData.invoiceTemplate[field].sibling;
            if (!invoiceDetails[field] && !invoiceDetails[sibling]) {
                errors[
                    field
                ] = `${stadiumData.invoiceTemplate[field].label} or ${stadiumData.invoiceTemplate[sibling].label} is required.`;
            }
        });

        return errors;
    };

    const getOrderData = async () => {
        try {
            const orderDOC = await serverData.call(
                "USSR_getPendingContextOrderId",
                { orderId: decodeParamString(encodedOrderId), contextId: brandContextData._id }
            );
            if (orderDOC) {
                const _ = require("lodash");

                const { stadium: stadiumData, orderedBy, invoPayments = [], amount = 0, basketAmount = 0, paymentInfo: { merchantServiceFee = "0", applyFeeExemption = false } } = orderDOC;
                const invoiceData = invoPayments[0];
                const { accountId, invoiceId, serviceDetails, amountToPay } = invoiceData;
                const { name = '', phone = '', email = '' } = orderedBy;

                const invoiceDetails = {
                    accountId,
                    invoiceId,
                    serviceDescription: serviceDetails,
                    amountToPay,
                    customerName: name,
                    customerEmail: email,
                    customerPhone: phone
                }

                const newStadiumData = _.cloneDeep(stadiumData);
                newStadiumData.stadTypes = ["STT_INVOICE"];

                if (!newStadiumData.invoiceTemplate) {
                    // Mocked templating
                    newStadiumData.invoiceTemplate = Bird;
                }

                setInvoiceDetails(invoiceDetails);
                setStadiumData(newStadiumData);
                setServiceFeeToShow(parseFloat(merchantServiceFee));
                setOrderTotalWithEverything(amount);
                setOrderSubtotal(basketAmount);
                getServiceFee(applyFeeExemption);

            } else {
                navigate("/error");
            }
        } catch (e) {
            console.log(e);
            navigate("/error");
        }
    }

    const getServiceFee = async (applyFeeExemption) => {
        // Compute order digital fee
        let digitalFeeObj = {};
        try {
            digitalFeeObj = await serverData.call("MIKO_getContextServiceFee", {
                contextId: brandContextData._id,
                exemptFee: applyFeeExemption,
            });
            setDigitalFeeObj(digitalFeeObj);
        } catch (e) {
            console.log(e);
            return;
        }
    };

    const completePayment = async () => {
        try {
            const feeLabel = digitalFeeObj?.serviceFeeInfo?.feeLabel
                ? digitalFeeObj.serviceFeeInfo.feeLabel
                : null;
            const newStoreUrl = `/${encodedOrderId}/payinvoice`;
            navigate(newStoreUrl, {
                state: {
                    orderID: decodeParamString(encodedOrderId),
                    encodedStadiumId: encodeParamString(stadiumData._id),
                    feeLabel,
                },
            });
        } catch (e) {
            notification.error({
                message: "Cannot Complete Payment",
                description: e.reason,
            });
            return;
        }
    };


    const handleFormChange = () => {
        let isFormValid = false;
        const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);

        const validationErrors = validateInvoiceDetails(invoiceDetails);

        if (Object.keys(validationErrors).length === 0 && !hasErrors) {
            if (!digitalFeeObj?.applyServiceFee) {
                isFormValid = true;
            }

            if (digitalFeeObj?.applyServiceFee && serviceFeeAgreement) {
                isFormValid = true;
            }
        } else {
            // console.log("Validation errors:", validationErrors);
        }

        setSubmittable(isFormValid);
    };

    let submitButtonTitle = `Continue to Payment - $${formatNumberWithCommas(
        orderTotalWithEverything
    )}`;

    const _renderFormField = (formObj, fieldName, formFields) => {
        const { display, label, type, sibling = false, selectorOptions = [], linkedSelector = null } = formObj;

        const handleLinkedChange = ({ inputVal, fieldName }) => {
            takeInput({ inputVal, fieldName });

            if (linkedSelector) {
                const linkObj = formFields.find(frm => frm.key === linkedSelector);
                const linkOptions = linkObj && linkObj.selectorOptions && Array.isArray(linkObj.selectorOptions) ? linkObj.selectorOptions : [];
                const linkedIndex = selectorOptions.findIndex(elem => elem === inputVal);

                if (linkOptions && linkOptions[linkedIndex]) {
                    takeInput({ inputVal: linkOptions[linkedIndex], fieldName: linkedSelector });
                }
            }
        }

        const showFormField = display;
        const formLabel = label;
        const isRequired = type === "required";

        const txtLabel = <Text className={styles.grayText}>{formLabel}</Text>;
        const requiredStar = isRequired ? (
            <AiFillStar
                fontSize={5}
                color="red"
                style={{ marginTop: 2, marginRight: 3 }}
            />
        ) : null;

        if (!showFormField) {
            return null;
        }

        if (sibling) {
            const siblingObj = stadiumData.invoiceTemplate[sibling];
            return (
                <Form.Item
                    initialValue={invoiceDetails[fieldName]}
                    className={styles.formItems}
                    name={fieldName}
                    rules={[
                        {
                            validator: (_, value) => {
                                if (!value && !invoiceDetails[fieldName]) {
                                    return Promise.reject(
                                        new Error(
                                            `Please input ${formLabel} and/or ${siblingObj.label}!`
                                        )
                                    );
                                }

                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <Row style={{ alignItems: "center" }}>
                        <Col
                            xs={{ span: 9 }}
                            sm={{ span: 9 }}
                            md={{ span: 10 }}
                            lg={{ span: 10 }}
                            xl={{ span: 11 }}
                        >
                            <Row>
                                <AiFillStar
                                    fontSize={5}
                                    color="red"
                                    style={{ marginTop: 2, marginRight: 3 }}
                                />
                                <Text className={styles.grayText}>{formLabel}</Text>
                            </Row>
                            {
                                Array.isArray(selectorOptions) && selectorOptions.length > 0 ?
                                    <Select
                                        style={{
                                            width: "100%",
                                        }}
                                        value={invoiceDetails[fieldName]}
                                        onChange={(e) => handleLinkedChange({ inputVal: e, fieldName })}
                                        options={selectorOptions.map(
                                            (option) => ({
                                                value: option,
                                                label: fieldName === "amountToPay" ? `$${option}` : option,
                                            })
                                        )}
                                    />
                                    :
                                    <Input
                                        value={invoiceDetails[fieldName]}
                                        readOnly={true}
                                        onChange={(e) =>
                                            takeInput({ inputVal: e.target.value, fieldName })
                                        }
                                    />
                            }
                        </Col>
                        <Col
                            xs={{ span: 6 }}
                            sm={{ span: 6 }}
                            md={{ span: 4 }}
                            lg={{ span: 4 }}
                            xl={{ span: 2 }}
                            className={styles.spaceCol}
                        >
                            <Text className={styles.text}>and/or</Text>
                        </Col>
                        <Col
                            xs={{ span: 9 }}
                            sm={{ span: 9 }}
                            md={{ span: 10 }}
                            lg={{ span: 10 }}
                            xl={{ span: 11 }}
                        >
                            <Row>
                                <AiFillStar
                                    fontSize={5}
                                    color="red"
                                    style={{ marginTop: 2, marginRight: 3 }}
                                />
                                <Text className={styles.grayText}>{siblingObj.label}</Text>
                            </Row>
                            {
                                Array.isArray(selectorOptions) && selectorOptions.length > 0 ?
                                    <Select
                                        style={{
                                            width: "100%",
                                        }}
                                        value={invoiceDetails[sibling]}
                                        onChange={(e) => handleLinkedChange({ inputVal: e, fieldName: sibling })}
                                        options={selectorOptions.map(
                                            (option) => ({
                                                value: option,
                                                label: fieldName === "amountToPay" ? `$${option}` : option,
                                            })
                                        )}
                                    />
                                    :
                                    <Input
                                        value={invoiceDetails[sibling]}
                                        readOnly={true}
                                        onChange={(e) =>
                                            takeInput({ inputVal: e.target.value, fieldName: sibling })
                                        }
                                    />
                            }
                        </Col>
                    </Row>
                </Form.Item>
            );
        }

        let rules = [];
        if (allRules[fieldName]) {
            const { _id: contextId } = brandContextData;

            // Custom rule for jepson petroleum (main & northwest)
            if (['0b1483324c68aff5014ae9b1', '3a37617ddc70d9b71bfa4e68'].includes(contextId) && fieldName === 'amountToPay') {
                rules = [
                    {
                        required: true,
                        message: "Please input Payment Amount!",
                    },
                    {
                        validator: (_, value) => {
                            if (value && value <= 0 && value > 40000) {
                                if (value > 40000) {
                                    return Promise.reject(new Error("Maximum payment amount allowed for invoicing is $40,000"));
                                } else {
                                    return Promise.reject(new Error("Payment amount is invalid"));
                                }
                            }
                            return Promise.resolve();
                        },
                    }
                ]
            } else {
                rules = allRules[fieldName];
            }
        } else {
            rules = [
                {
                    required: isRequired,
                    message: `Please input a ${formLabel}`,
                },
            ];
        }

        return (
            <>
                <Row>
                    {requiredStar}
                    {txtLabel}
                </Row>
                <Form.Item
                    initialValue={
                        invoiceDetails[fieldName] ? invoiceDetails[fieldName] : null
                    }
                    className={styles.formItems}
                    name={fieldName}
                    rules={rules}
                >
                    {
                        Array.isArray(selectorOptions) && selectorOptions.length > 0 ?
                            <Select
                                style={{
                                    width: "100%",
                                }}
                                value={invoiceDetails[fieldName]}
                                onChange={(e) => handleLinkedChange({ inputVal: e, fieldName })}
                                placeholder={isRequired ? null : "(Optional)"}
                                options={selectorOptions.map(
                                    (option) => ({
                                        value: option,
                                        label: fieldName === "amountToPay" ? `$${option}` : option,
                                    })
                                )}
                            />
                            :
                            fieldName === 'amountToPay' ?
                                <InputNumber
                                    prefix='$'
                                    value={invoiceDetails[fieldName]}
                                    placeholder={isRequired ? null : "(Optional)"}
                                    onChange={(e) => takeInput({ inputVal: e, fieldName })}
                                    stringMode={true}
                                    style={{ width: '100%' }}
                                    step={0.01}
                                    precision={2}
                                    readOnly={true}
                                />
                                :
                                <Input
                                    value={invoiceDetails[fieldName]}
                                    placeholder={isRequired ? null : "(Optional)"}
                                    readOnly={true}
                                    onChange={(e) => takeInput({ inputVal: e.target.value, fieldName })}
                                />
                    }
                </Form.Item>
            </>
        );
    };

    const takeInput = ({ inputVal, fieldName }) => {
        if (fieldName === "amountToPay") {
            inputVal = Number(inputVal);
            setOrderSubtotal(inputVal);
        }
        setInvoiceDetails((prevState) => ({ ...prevState, [fieldName]: inputVal }));
    };

    const formatServiceFee = (digitalFeeObj) => {
        const feePercentage = digitalFeeObj?.serviceFeeInfo?.feePercentage;
        const feeCents = digitalFeeObj?.serviceFeeInfo?.feeCents;

        const percentageString = feePercentage ? `${feePercentage}%` : "";
        const feeString = feeCents ? `$${(feeCents / 100).toFixed(2)}` : "";

        if (percentageString && feeString) {
            return `${percentageString} + ${feeString}`;
        } else {
            return percentageString || feeString;
        }
    };

    let imgWidth = 180;

    if (!stadiumData) {
        return null;
    }

    const formFields = [];
    for (const [key, value] of Object.entries(stadiumData?.invoiceTemplate)) {
        const newObj = { ...value, key };
        formFields.push(newObj);
    }

    // Sort the formFields array based on the renderPosition property
    formFields.sort((a, b) => (a.renderPosition || 0) - (b.renderPosition || 0));

    //For confirm service fee show different description text for The Mental Health Gala Payments
    const tooltipText = "Your transaction is processed by a secure third-party provider. Your personal and financial information is secure and PCI DSS compliant when processed through our payment portal. A transaction receipt will be sent to the contact information in the payment form."

    const { _id: contextId } = brandContextData;

    return (
        <div>
            {!loadingBrandContextData && brandContextData && stadiumData ? (
                <Col
                    xs={{ span: 24 }}
                    sm={{ span: 20, offset: 2 }}
                    md={{ span: 20, offset: 2 }}
                    lg={{ span: 18, offset: 3 }}
                    xl={{ span: 16, offset: 4 }}
                    className={styles.container}
                >
                    {stadiumData?.imageUrl ? (
                        <Row justify={"center"} className={styles.logoContainer}>
                            <Image
                                width={mobile ? "50%" : imgWidth}
                                preview={false}
                                src={stadiumData.imageUrl}
                                style={{
                                    maxHeight: 180,
                                    minHeight: 75,
                                    objectFit: "contain",
                                }}
                                onClick={() => navigate("/")}
                            />
                        </Row>
                    ) : null}
                    <Col className={styles.content}>
                        {stadiumData?.name ? (
                            <Row style={{ marginBottom: 20, justifyContent: "center" }}>
                                <Col
                                    span={24}
                                    style={{ justifyContent: "center", display: "flex" }}
                                >
                                    <Text style={{ fontWeight: "bold" }}>{'PAYEE'}</Text>
                                </Col>
                                <Col
                                    span={24}
                                    style={{ justifyContent: "center", display: "flex" }}
                                >
                                    <Text className={styles.grayText} style={{ fontSize: 20 }}>
                                        {stadiumData.name}
                                    </Text>
                                </Col>
                            </Row>
                        ) : null}
                        <Form
                            form={form}
                            layout="vertical"
                            size="large"
                            name="basic"
                            wrapperCol={{
                                span: 24,
                            }}
                            initialValues={{
                                remember: true,
                            }}
                            autoComplete="off"
                            onFieldsChange={handleFormChange}
                        >
                            {formFields.map((field) => (
                                <React.Fragment key={field.key}>
                                    {_renderFormField(field, field.key, formFields)}
                                </React.Fragment>
                            ))}

                            {(['0b1483324c68aff5014ae9b1', '3a37617ddc70d9b71bfa4e68'].includes(contextId)) ?
                                <Row
                                    style={{ marginBottom: 30 }}
                                >
                                    <AiFillStar
                                        fontSize={5}
                                        color="red"
                                        style={{ marginTop: 2, marginRight: 3 }}
                                    />
                                    <Text className={styles.disclaimerText}>{`Maximum payment amount allowed for invoicing is $40,000`}</Text>
                                </Row>
                                :
                                null}


                            {digitalFeeObj?.applyServiceFee && (digitalFeeObj?.serviceFeeInfo && digitalFeeObj?.serviceFeeInfo?.feeAcknowledgement) ? (
                                <Col
                                    xs={{ span: 22, offset: 1 }}
                                    sm={{ span: 22, offset: 1 }}
                                    md={{ span: 22, offset: 1 }}
                                    lg={{ span: 18, offset: 3 }}
                                    xl={{ span: 12, offset: 6 }}
                                    style={{ marginBottom: 30 }}
                                >
                                    <Form.Item
                                        name="agreement"
                                        valuePropName="checked"
                                        rules={
                                            []
                                        }
                                    >
                                        <Checkbox
                                            onChange={(e) => setServiceFeeAgreement(e.target.checked)}
                                            style={{
                                                "--background-color": token.colorSecondary,
                                                "--border-color": token.colorSecondary,
                                                color: "gray",
                                            }}
                                            checked={serviceFeeAgreement}
                                        >
                                            I acknowledge the{" "}
                                            {digitalFeeObj?.serviceFeeInfo?.feeLabel
                                                ? digitalFeeObj.serviceFeeInfo.feeLabel
                                                : "Digital Service Fee"}
                                            <span style={{ color: "red" }}>*</span>
                                        </Checkbox>
                                        <Tooltip title={tooltipText}>
                                            <FaInfoCircle
                                                name="info"
                                                style={{ fontSize: 15, color: "gray" }}
                                            />
                                        </Tooltip>
                                    </Form.Item>
                                </Col>
                            ) : null}
                            <Col
                                xs={{ span: 22, offset: 1 }}
                                sm={{ span: 22, offset: 1 }}
                                md={{ span: 22, offset: 1 }}
                                lg={{ span: 18, offset: 3 }}
                                xl={{ span: 12, offset: 6 }}
                                style={{ marginBottom: 40 }}
                            >
                                <Row justify={"space-between"}>
                                    <Text className={styles.totalText}>Subtotal</Text>
                                    <Text
                                        className={
                                            orderSubtotal ? styles.totalText : styles.redText
                                        }
                                    >
                                        {orderSubtotal
                                            ? `$${formatNumberWithCommas(orderSubtotal)}`
                                            : "Please input payment amount"}
                                    </Text>
                                </Row>
                                {digitalFeeObj?.applyServiceFee ? (
                                    <Row justify={"space-between"}>
                                        <Text className={styles.totalText}>
                                            {digitalFeeObj?.serviceFeeInfo?.feeLabel
                                                ? `${digitalFeeObj.serviceFeeInfo.feeLabel
                                                } (${formatServiceFee(digitalFeeObj)})`
                                                : `Digital Service Fee (${formatServiceFee(
                                                    digitalFeeObj
                                                )})`}
                                        </Text>
                                        <Text className={styles.totalText}>
                                            {orderSubtotal
                                                ? `$${Number(serviceFeeToShow).toFixed(2)}`
                                                : "..."}
                                        </Text>
                                    </Row>
                                ) : null}
                                <Divider className={styles.totalDivider} />
                                <Row justify={"space-between"}>
                                    <Text className={styles.totalText}>Total</Text>
                                    <Text className={styles.totalText}>
                                        {orderSubtotal
                                            ? `$${formatNumberWithCommas(orderTotalWithEverything)}`
                                            : "..."}
                                    </Text>
                                </Row>
                                <Button
                                    disabled={!submittable}
                                    htmlType="submit"
                                    className={styles.submitButton}
                                    onClick={completePayment}
                                >
                                    {submitButtonTitle}
                                </Button>
                            </Col>
                        </Form>
                    </Col>
                </Col>
            ) : null}
        </div>
    );
};

export default PreFilledInvoicePaymentForm;
